'use strict';

import Mustache from 'mustache';
import _ from 'underscore';
import $ from 'jquery';
import { BaseView } from './views/base-view';
import { Favorites } from './favorites';
import { loc } from './localizations';
import popover from './popover';
import './tab-menu';
import datalayerActions from './datalayer-actions';

const FIRST_PAGE = 1;

const DOM = {
  //main parts
  form: '.product-finder-form',
  searchResultsHeader: '.search-result__header',
  searchResults: '.search-result__content',
  showMoreResults: 'button[name="show-more-results"]',
  searchCriteriaTab: '.product-finder__search-criterias',
  searchResultTab: '.product-finder__search-results',

  resultsProgress: 'progress#resultsGauge',
  resultsVisible: '#resultsVisible',
  resultsTotal: '#resultsTotal',

  //templates
  searchResultTemplate: '.search-result-template',
  carCardTemplate: '.car-card-template',
  additionalCardTemplate: '.additional-card-template',
  searchResultHeaderTemplate: '.search-result-header-template',
  dropdownMultiOptionsTemplate: '.dropdown-multi-options-template',
  sortOrderTemplate: '.search-criteria-sort-order-template',
  searchCriteriaTabTemplate: '.search-criteria-tab-template',
  moreResultsGaugeTemplate: '.more-results-gauge-template',
};

/**
 * ProductFinderFrom class hold logic for client side product search form.
 */
class ProductFinderForm extends BaseView {
  /**
   * Constructor
   * @param {string|jqQuery.element|HTMLElement} el, product finder HTML element
   */
  constructor(el) {
    super(el, DOM);
    //update filters if page has query parameters
    this._updateSearchCriterias(this._serializeArray());

    //Templates are kept on Stage side /Pages -> Mustache Document Footer -> Mustache templates
    this._searchResultTemplate = this.$dom.searchResultTemplate.html();
    this._carCardTemplate = this.$dom.carCardTemplate.html();
    this._additionalCardTemplate = this.$dom.additionalCardTemplate.html();
    this._searchResultHeaderTemplate = this.$dom.searchResultHeaderTemplate.html();
    this._dropdownMultiOptionsTemplate = this.$dom.dropdownMultiOptionsTemplate.html();
    this._moreResultsGaugeTemplate = this.$dom.moreResultsGaugeTemplate.html();
    this._pictureSource = document.querySelector('[data-id="pictureSource"]')?.innerHTML;

    //Site main search page path. Used after filter changes
    this._searchPageUrl = location.protocol + '//' + location.host + (window.SEARCHPAGE || '');

    this._page = FIRST_PAGE;

    this.$el.find('#advanced-filters').on('shown.bs.collapse', this._onAdvancedSectionOpen.bind(this));
    this.$el.find('#advanced-filters').on('hidden.bs.collapse', this._onAdvancedSectionCollapse.bind(this));

    //Handles products favorite-state
    this._favorites = new Favorites(this.$dom.searchResultTab);
    this._favorites.updateAmount();

    popover.createProductListPopovers(this.$dom.searchResultTab);

    //add element we can send focus before loading new items
    this.$dom.searchResults.find('.search-result-rows').append('<span class="page-end" tabindex="-1"></span>');

    this.formTimeOutId = null;

    //get correct search position from sessionStorage
    this._useSessionStorage(this);

    // handle data layer push.
    this._handleDataLayerPush();
  }

  _onAdvancedSectionOpen(e) {
    this.$el.find('.search-criterias-advanced-filter-link').text(loc('search_criterias_advanced_filters_up'));
  }

  _onAdvancedSectionCollapse() {
    this.$el.find('.search-criterias-advanced-filter-link').text(loc('search_criterias_advanced_filters_down'));
  }

  /**
   * Update search page url
   * @param {object} params search parameters
   * @param {boolean} append makes pagination work
   */
  updateSearchUrl(params, append) {
    let _params = _.clone(params);

    //Remove some params from url
    const parametersNotIncludedInURL = ['vehicleUuids', 'campaigns', 'page'];

    _params = _params.filter(param => {
      const key = param.name.replace('[]', '');
      return parametersNotIncludedInURL.indexOf(key) === -1;
    });

    const urlParams = '?' + $.param(_params);

    //replace or push state, depending if url is landing page or general search page
    if (!location.href.includes(window.SEARCHPAGE)) {
      window.history.pushState(null, '', this._searchPageUrl + urlParams);
    } else {
      window.history.replaceState(null, '', urlParams);
    }
  }

  /**
   * Makes a vehicle search
   * @param {object} params, search parameters
   * @return {jQuery.Promise} resolved after successful request
   */
  search(params) {
    return $.ajax({
      url: '/stage-ajax/vehicle-search',
      type: 'get',
      data: params,
    });
  }

  /**
   * Gets location filters
   * @param {object} params, search parameters
   * @return {jQuery.Promise} resolved after successful request
   */
  getLocationFilters(params) {
    return $.ajax({
      url: '/stage-ajax/get-location-filters',
      type: 'get',
      data: params,
    });
  }

  _updateResultsProgress(visibleCount, totalCount, moreResultsAvailable) {
    // Can't use this.$dom because it may be outdated at this point
    const resultsTotal = $(DOM.resultsTotal);
    const resultsProgress = $(DOM.resultsProgress);
    const resultsVisible = $(DOM.resultsVisible);
    resultsVisible.text(visibleCount);
    resultsTotal.text(totalCount);
    resultsProgress.val(visibleCount);
    resultsProgress.prop('max', totalCount);

    if (!moreResultsAvailable) {
      this.$dom.searchResults.find(this.dom.showMoreResults).hide();
    }
  }

  /**
   * Handles the selection of the license class parameter based on the search type.
   * 
   * This function ensures that the `license_class[]` parameter is removed from the
   * `_params` array if the selected search type is not "moottoripyorat". The operation
   * is intended to be performed on a copy of `params`, so if "moottoripyorat"
   * is reselected as the search type, the previously selected license classes are
   * automatically reactivated.
   * 
   * @param {Object[]} paramsCopy - A copy of the parameters array.
   * @returns {Object[]} - The modified parameters array.
   */
  _handleLicenseClassSelection(paramsCopy) {
    const motorCycleSearchType = paramsCopy.find(param => param.name === 'search_type' && param.value === 'moottoripyorat');
    const licenseClassParam = paramsCopy.find(param => param.name === 'license_class[]');
    if (!motorCycleSearchType && licenseClassParam) {
      paramsCopy = paramsCopy.filter(param => param.name !== 'license_class[]');
    }
    return paramsCopy;
  }

  /**
   * Does a product search and shows all search results
   *
   * @param {array} params, optional can be left null
   * @param {boolean} append, when true search results appends result are, otherwise old results are replaced
   */
  doSearch(params = null, append = false) {
    const self = this;
    let _params = params ? params : this._serializeArray();

    _params = _params.concat(
      this._getCampaignsUuids().map(uuid => {
        return { name: 'campaigns[]', value: uuid };
      }),
    );

    _params = this._handleNewCarSelection(_params);

    _params = this._handleHybridSelection(_params);

    _params = this._handleLicenseClassSelection(_params);

    // Remove model params if corresponding make not checked also
    const selectedManufacturers = _params.filter(param => param.name === 'manufacturer[]').map(({ value }) => value);
    const modelParamsToRemove = _params
      .filter(param => param.name === 'model[]')
      .filter(param => !selectedManufacturers.some(manu => param.value.includes(manu)));
    _params = _params.filter(param => modelParamsToRemove.indexOf(param) < 0);



    if (append) {
      this._page++;
    } else {
      this._page = FIRST_PAGE;
    }

    _params.push({ name: 'page', value: this._page });

    const sortOrder = this.getCurrentSortOrder();
    if (sortOrder) {
      _params.push({ name: 'sort', value: sortOrder });
    }

    this._updateSearchCriterias(_params);
    this.updateSearchUrl(_params, append);

    if (document.querySelector('meta[property="pageId"]')) {
      _params.push({ name: 'pageId', value: document.querySelector('meta[property="pageId"]').content });
    }

    // If the pictureSource partial was found, then request result with webp images
    if (this._pictureSource) _params.push({ name: 'webp', value: 1 });

    // Add indication to include reserved vehicles
    _params.push({ name: 'includeReserved', value: 1 });

    this.search(_params).done(response => {
      this.$dom.searchResults.find('.loading').hide();
      this.$dom.searchResults.find(this.dom.showMoreResults).removeClass('btn--loading').prop('disabled', false);

      if (append && response.products.length === 0) {
        return;
      }

      this._updateSearchResultHeader(response, _params);
      this._updateTitle(response);
      response = this._setupCarCard(response);

      const partials = {
        carCard: this._carCardTemplate,
        resultsGauge: this._moreResultsGaugeTemplate,
        additionalCard: this._additionalCardTemplate,
      };
      if (this._pictureSource) partials.pictureSource = this._pictureSource;

      const data = Mustache.render(this._searchResultTemplate, response, partials);

      if (append) {
        let $vehicles = $(data).find('.card');
        self.$dom.searchResults.find('.search-result-rows').append($vehicles);
      } else {
        self.$dom.searchResults.html(data);
      }

      //add element we can send focus before loading new items
      self.$dom.searchResults.find('.search-result-rows').append('<span class="page-end" tabindex="-1"></span>');

      this._updateResultsProgress(self.$dom.searchResults.find('.car-card').length, response.resultAvailable, response.moreResultAvailable);

      // JRINTCONT-767: handle data layer push on AJAX update
      this._handleDataLayerPush();
    });
  }

  attach() {
    this.$dom.form.submit(e => {
      e.preventDefault();
      this.doSearch();
    });

    this.$dom.form.on('reset', e => {
      e.preventDefault();

      setTimeout(() => {
        //wait a few ms and then trigger change event to make sure that reset event has finished.
        this.resetForm();
        this.$dom.form.submit();
      }, 5);
    });

    $('.product-finder').on('click', '.js-remove-all-criterias', () => {
      this.$dom.form.trigger('reset');
    });

    this.$dom.form.change(e => {
      clearTimeout(this.formTimeOutId);
      this.$dom.searchResults.find('.loading').show();

      // Remove SEO text when user selects a different manufacturer, so that the SEO text and selected manufacturer no longer match.
      if (e.target.name === 'manufacturer[]') {
        this.$dom.searchResultsHeader.find('.list-details').html('');
      }

      // If type is changed, reset korimalli
      if (e.target.name === 'search_type') {
        const form = document.querySelector('.product-finder-form');
        const formInputs = form.querySelectorAll('select[name="korimalli[]"]');

        const salesTypes = document.querySelectorAll('[data-key="usedandnewcars"] input[name="sales_type[]"]');

        Array.from(formInputs).forEach(control => {
          control.checked = false;
          control.value = '';
          control.dispatchEvent(new Event('change'));
        });
        //clear sales types before making them hidden. Might cause some confusion if user selects  uudet autot and then selects moottoripyörät.
        if (e.target.value === 'moottoripyorat') {
          Array.from(salesTypes).forEach(control => {
            control.checked = false;
            control.dispatchEvent(new Event('change'));
          });
        }
      }

      this.formTimeOutId = setTimeout(() => {
        this.$dom.form.submit();
      }, 1000);
    });

    this.$dom.form.find('.colorsfilter__color').click(this._onColorClicked.bind(this));
    this.$dom.form.find('.license-number__input.reg_number').on('keypress', this._onRegInput.bind(this));

    this.$dom.searchResults.on('click', 'button[name="show-more-results"]', () => {
      this._showMoreSearchResults();
    });

    const availabilityButtons = document.getElementsByClassName('availability-button');
    for (var i = 0; i < availabilityButtons.length; i++) {
      availabilityButtons[i].addEventListener(
        'click',
        function (e) {
          e.preventDefault();
          $('input[name="availability[]"]').prop('checked', false);
          $('input[name="availability[]"][value=' + e.target.search.split('#')[0].split('=')[1] + ']').prop('checked', true);

          this.$dom.form.change();

          $('html, body').animate(
            {
              scrollTop: $('#search-results').offset().top,
            },
            250,
          );
        }.bind(this),
      );
    }

    this.$dom.searchResultsHeader.find('select[name="sort"]').change(() => this.$dom.form.change());

    this._favorites.attachLikeEvents();
    this._favorites.updateAmount();

    popover.createProductListPopovers(this.$dom.searchResultTab);

    this._addChipEvents();
    this._addStylingListeners();
  }

  // Styling-related code for radio buttons and checkboxes (cannot be handled with CSS)
  _addStylingListeners() {
    const checkboxes = document.getElementById('product-finder').querySelectorAll('input[type="checkbox"]');
    const radiobuttons = document.getElementById('product-finder').querySelectorAll('input[type="radio"]');
    for (const elem of checkboxes) {
      if (elem.checked) {
        elem.parentNode.classList.add('pressed');
      }
      elem.addEventListener('change', function (e) {
        e.target.parentNode.classList.toggle('pressed');
      });
    }
    for (const elem of radiobuttons) {
      if (elem.checked) {
        elem.parentNode.classList.add('pressed');
      }
      elem.addEventListener('change', function (e) {
        for (const input of e.target.parentNode.parentNode.getElementsByTagName('input')) {
          input.checked ? input.parentNode.classList.add('pressed') : input.parentNode.classList.remove('pressed');
        }
      });
    }
  }

  getCurrentSortOrder() {
    const sortOrder = this.$dom.searchResultsHeader.find('select[name="sort"] option:selected').val() || '';
    return sortOrder;
  }

  /**
   * Handle availability input visibility
   * @param {Object} params Search parameters
   * @returns {Object} Modified search parameters
   * @private
   */
  _handleNewCarSelection(params) {
    const salesTypes = [];

    for (const item of params) {
      if (item.name === 'sales_type[]') {
        salesTypes.push(item.value);
      }
    }

    const availabilityContainer = this.$dom.searchCriteriaTab[0].querySelector('.search-criteria__vehicletype[data-key="availability"]');
    const availabilityInputs = availabilityContainer.querySelectorAll('input[name="availability[]"]');

    // Hide/show availability fields
    if (!salesTypes.includes('usedCar') && salesTypes.length > 0) {
      availabilityContainer?.classList.remove('hidden');
    } else {
      // Uncheck availability inputs
      for (const input of availabilityInputs) {
        input.checked = false;
      }

      params = params.filter(item => item.name !== 'availability[]');

      availabilityContainer?.classList.add('hidden');
    }

    return params;
  }

  /**
   * Handle hybrid type select visibility
   * @param {Object} params Search parameters
   * @returns {Object} Modified search parameters
   * @private
   */
  _handleHybridSelection(params) {
    const fuelTypes = [];

    for (const item of params) {
      if (item.name === 'fuel_type[]') {
        fuelTypes.push(item.value);
      }
    }

    const hybridTypeContainer = this.$dom.searchCriteriaTab[0].querySelector('.search-criteria__vehicletype[data-key="hybrid_type"]');
    const hybridTypeInputs = hybridTypeContainer.querySelectorAll('select[name="hybrid_type[]"] option');

    // Hide/show hybridtype fields
    if (fuelTypes.includes('hybrid')) {
      hybridTypeContainer?.classList.remove('hidden');
    } else {
      // Unselect hybridtype options
      for (const input of hybridTypeInputs) {
        input.selected = false;
      }

      params = params.filter(item => item.name !== 'hybrid_type[]');

      hybridTypeContainer?.classList.add('hidden');
    }

    document.querySelector('select[name="hybrid_type[]"]').dispatchEvent(new Event('change'));

    return params;
  }

  _updateSearchCriterias(params) {
    this._updateManufacturers(params);
    this._updateModels(params);
    this._updateBodyTypes(params);
    this._updateModelYears(params);
    this._updateDealerships(params);
    this._updateSorting(params);
    this._updateCaravanFilters(params);
    this._updateSearchFieldsVisibility(params);
    this._updateChips(params);
  }

  _updateChips() {
    const params = [];

    const formElement = document.querySelector('.product-finder-form');
    const chipTarget = document.querySelector('.search-result__chipper');
    const chipTemplate = document.querySelector('.chip-template')?.innerHTML;

    // Get all non-disabled selects
    const selectedSelects = formElement.querySelectorAll('select:not(disabled)');
    let selectedOptions = [];
    for (const selectElement of selectedSelects) {

      // For reasons unknown, the querySelector above still occasionally matches disabled selects.
      // Haven't been able to determine why.
      // In any case, to avoid undesired effects we double-check that the element is indeed enabled.
      if (selectElement.disabled) continue;

      // Get all selected options that are not disabled and have a value
      const selectedOptions = Array.from(selectElement.options)
        .filter(option => option.selected && !option.disabled && option.value);

      // Add a chip for each selected option
      for (const option of selectedOptions) {
      const value = option.value;
      const name = selectElement.name;
      const label = option.text;
      if (value && name && label) params.push({ name, value, label });
      }
    }

    // Do the same for checkboxes
    const checkboxes = formElement.querySelectorAll('input[type="checkbox"]:checked:not(disabled)');
    checkboxes.forEach(checkbox => {
      const labelElement = checkbox.closest('label');
      const value = checkbox.value;
      const name = checkbox.name;
      const label = labelElement.textContent.trim();
      if (value && name && label) params.push({ name, value, label });
    });

    // And text inputs
    const textInputs = formElement.querySelectorAll('input[type="text"]');
    textInputs.forEach(input => {
      const labelElement = input.closest('label');
      const value = input.value;
      const name = input.name;
      const label = labelElement?.textContent.trim();
      if (value && name && label) params.push({ name, value, label });
    });

    // Render chips
    chipTarget.innerHTML = Mustache.render(chipTemplate, { params });
  }

  _addChipEvents() {
    const chipWrapper = document.querySelector('.search-result__chipper');
    const formElement = document.querySelector('.product-finder-form');
    chipWrapper.addEventListener('click', e => {
      const element = e.target;
      if (element.classList.contains('chip')) {
        //clear a single form input with buttons

        const name = element.name;
        const value = element.value;
        if (name && value) {
          const inputsWithName = formElement.querySelectorAll(`[name="${name}"]`);
          let targetInput = false;

          for (let i = 0; i < inputsWithName.length && !targetInput; i++) {
            if (inputsWithName[i].value === value && !inputsWithName[i].options) {
              targetInput = inputsWithName[i];
            } else if (inputsWithName[i].options) {
              targetInput = Array.from(inputsWithName[i].options).find(opt => opt.selected && opt.value === value);
            }
          }

          if (targetInput) {
            targetInput.selected = false;
            targetInput.checked = false;
            if (targetInput.type === 'text') targetInput.value = '';
            const event = new Event('change', { bubbles: true });
            targetInput.dispatchEvent(event);
          }

          element.remove();
        }
      }
    });
  }

  /**
   * Show / hide caravan spesific filters based on selected parameters
   *
   * @param {*} params query parameters
   * @memberof ProductFinderForm
   */
  _updateCaravanFilters(params = []) {
    const $caravanFilters = this.$dom.form.find('.caravan-filter');
    const $caravanInputs = this.$dom.form.find('.caravan-filter select');
    const selectedSearchTypes = this._getSelectedSearchTypes(params);
    const caravansSelected = selectedSearchTypes.includes('matkailuautot-ja-vaunut');

    $caravanFilters.toggleClass('d-none', !caravansSelected);
    $caravanInputs.attr('disabled', !caravansSelected);
  }

  /**
   * Update sorting input options based on selected parameters
   *
   * @param {*} params query parameters
   * @memberof ProductFinderForm
   */
  _updateSorting(params = []) {
    const $sortOptions = this.$dom.searchResultsHeader.find('select[name="sort"] option');
    const selectedSalesTypes = params?.filter(param => param.name === 'sales_type[]').map(param => param.value) || [];

    $sortOptions.each(function () {
      const $option = $(this);
      const optionSalesTypes = $option.data('salesTypes')?.split(',') || [];
      let showOption = true;

      if (selectedSalesTypes.length && optionSalesTypes.length) {
        showOption = optionSalesTypes.some(type => selectedSalesTypes.includes(type));
      }

      $option.attr('hidden', !showOption);
      $option.attr('disabled', !showOption);
      if (!showOption) {
        $option.attr('selected', false);
      }
    });
  }

  /**
   * Update vehicle models based on selected parameters
   *
   * @param {*} params query parameters
   * @memberof ProductFinderForm
   */
  _updateModels(params = []) {
    const $modelInput = this.$dom.form.find('select[name="model[]"]');
    const $modelGroups = $modelInput.find('optgroup');
    const selectedMakes = params?.filter(param => param.name === 'manufacturer[]').map(param => param.value) || [];
    const selectedSalesTypes = params?.filter(param => param.name === 'sales_type[]').map(param => param.value) || [];
    const selectedSearchTypes = this._getSelectedSearchTypes(params);

    $modelGroups.attr('hidden', true);
    $modelGroups.find('option').attr('hidden', true).attr('disabled', true);

    $modelGroups.each(function () {
      const $group = $(this);
      const $options = $group.find('option');
      let showGroup = false;

      if (selectedMakes.includes($group.attr('label'))) {
        showGroup = true;
      }

      $group.attr('hidden', !showGroup);
      $options.each(function () {
        const $option = $(this);
        const optionSalesTypes = $option.data('salesTypes')?.split(',') || [];
        const optionSearchTypes = $option.data('searchTypes')?.split(',') || [];
        let showOption = showGroup;

        if (showOption && selectedSearchTypes.length && optionSearchTypes.length) {
          showOption = selectedSearchTypes.some(type => optionSearchTypes.includes(type));
        }

        if (showOption && optionSalesTypes.length && selectedSalesTypes.length) {
          showOption = selectedSalesTypes.some(type => optionSalesTypes.includes(type));
        }

        if (showOption) {
          $option[0].hidden = false;
          $option[0].disabled = false;
        } else {
          $option[0].selected = false; // can't be done with jquery since it doesn't work for some reason :)
        }
      });
    });

    $modelInput.attr('disabled', selectedMakes.length === 0);
    document.querySelector('select[name="model[]"]').dispatchEvent(new Event('change'));
  }

  /**
   * Update dealership options based on selected parameters
   *
   * @param {*} params query parameters
   * @memberof ProductFinderForm
   */
  _updateDealerships(params = []) {
    const $dealershipSelect = this.$dom.form.find('select[name="org_id[]"]');

    this.getLocationFilters(params).done(response => {
      const selectedFilters = Array.from($dealershipSelect[0].selectedOptions);
      const selectedOrgIds = selectedFilters.map(item => item['value']);

      //save .dropdown--clear-close-buttons
      const clearCloseListItem = $dealershipSelect[0].parentNode.querySelector('.dropdown--clear-close-buttons');

      // Remove all options
      while ($dealershipSelect[0].options.length) {
        $dealershipSelect[0].remove(0);
      }

      // Push previously selected filter/s to response array if the response doesn't contain it/them.
      selectedFilters.forEach(filter => {
        if (!response.some(f => f.value === filter.value) && filter.value) {
          response.push({ text: filter.text, value: filter.value });
        }
      });

      // Add new options to select & determine if select should be selected or not.
      response.forEach(({ text, value, selected = false }) => {
        if (selectedOrgIds.includes(value)) {
          selected = true;
        }

        $dealershipSelect[0].add(new Option(text, value, false, selected));
      });

      //move clearCloseListItem to the end of the list
      if (clearCloseListItem) {
        // pick ul list
        const ulList = $dealershipSelect[0].parentNode.querySelector('.multiselect-list');
        // put clearCloseListItem to the end of the list
        // this is stupid, but it works! DO NOT COPY PASTE ME EVER!
        // if you want to fix this, simply remove jquery and use vanilla JS
        setTimeout(() => {
          ulList.parentNode.appendChild(clearCloseListItem);
        }, 100);
      }
    });
  }

  /**
   * Update vehicle search model year beased on selected filters
   *
   * @param {*} params query parameters
   * @memberof ProductFinderForm
   */
  _updateModelYears(params = []) {
    const $modelYearInputs = this.$dom.form.find('select[name^="manufacture_year"]');
    const $modelYearOptions = $modelYearInputs.find('option');

    const selectedSalesTypes = params?.filter(param => param.name === 'sales_type[]') || [];
    const newVehiclesSelected = selectedSalesTypes.length === 1 && selectedSalesTypes[0].value === 'newCar';
    const currentYear = new Date().getFullYear();

    $modelYearOptions.each(function () {
      const $option = $(this);
      let showOption = true;
      const value = parseInt($option.val());

      //if new vehicles are selected, show years that are 3 years older than this year.
      if (newVehiclesSelected && value && value - currentYear < -2) {
        showOption = false;
      }

      $option.attr('hidden', !showOption);
      $option.attr('disabled', !showOption);
      if (!showOption) {
        $option.attr('selected', false);
      }
    });
  }

  /**
   * Update manufacturer options visibility based on selected parameters.
   *
   * @param {*} params query parameters
   * @memberof ProductFinderForm
   */
  _updateManufacturers(params = []) {
    const $manufacturerOptions = this.$dom.form.find('select[name="manufacturer[]"] option');

    const selectedSearchTypes = this._getSelectedSearchTypes(params);
    const selectedSalesTypes = params?.filter(param => param.name === 'sales_type[]').map(param => param.value) || [];

    $manufacturerOptions.each(function () {
      const $option = $(this);
      let showOption = true;

      // hide placeholder option
      if (!$option[0].value) {
        showOption = false;
      }

      // show if current search type in data-search-types
      if (selectedSearchTypes.length && $option.data('searchTypes')) {
        const optionSearchTypes = $option.data('searchTypes')?.split(',');
        showOption = selectedSearchTypes.some(type => optionSearchTypes.includes(type));
      }

      // show if current sales type in data-sales-types
      if (showOption && selectedSalesTypes.length && $option.data('salesTypes')) {
        const optionSalesTypes = $option.data('salesTypes')?.split(',');
        showOption = selectedSalesTypes.some(type => optionSalesTypes.includes(type));
      }

      // show selected option regardless of type (except if it's the placeholder)
      if ($option[0].selected && $option[0].value) {
        showOption = true;
      }

      $option.attr('hidden', !showOption);
      $option.attr('disabled', !showOption);
      if (!showOption) {
        $option[0].selected = false;
      }
    });

    document.querySelector('select[name="manufacturer[]"]').dispatchEvent(new Event('change'));
  }

  /**
   * Return array of selected search_type values.
   *
   * @param {Array} [params] query parameters
   * @returns {Array} selected search_type values
   */
  _getSelectedSearchTypes(params) {
    return params?.filter(param => param.name === 'search_type').map(param => param.value) || [];
  }

  /**
   * Update body type inputs to be visible / hidden based on selected parameters.
   *
   * @param {Array} [params=[]] query parameters
   * @memberof ProductFinderForm
   */
  _updateBodyTypes(params = []) {
    if (!params) params = [];
    const selectedSearchTypes = this._getSelectedSearchTypes(params);
    const bodyTypesSelect = document.querySelector('select#search-criteria__body-type');
    const bodyTypeOptions = document.querySelectorAll('#search-criteria__body-type option');
    bodyTypesSelect.disabled = selectedSearchTypes.length === 0;
    for (const option of bodyTypeOptions) {
      if (option.dataset.vehicleType === undefined) {
        continue;
      }
      /*
        option elements of body types include attribute 'data-vehicle-type',
        which contains a comma-separated list that specifies the vehicle
        type (=search_type) where the body type should be enabled
      */

      const optionVehicleTypes = option.dataset.vehicleType.split(',');
      for (const searchType of selectedSearchTypes) {
        const showBodyType = optionVehicleTypes.includes(searchType);
        if (!showBodyType) {
          option.disabled = true;
          option.selected = false;
        } else {
          option.disabled = false;
        }
      }
    }
  }

  /**
   * When a show more result button is clicked this function is called.
   */
  _showMoreSearchResults() {
    this.$dom.searchResults.find(this.dom.showMoreResults).addClass('btn--loading').prop('disabled', true);

    //before loading more content, move keyboard focus to result end (for keyboard usability)
    const pageEnd = this.$el.find('.page-end').last();
    if (pageEnd.length) {
      const scrollPos = $(document).scrollTop();
      pageEnd.focus();
      $(document).scrollTop(scrollPos); //restore scroll position to avoid glitch
    }

    //load more results
    this.doSearch(null, true);
  }

  /**
   * Hack to revert the product list to same position when coming
   * with the back button. This is done by saving the product list markup to
   * session storage and setting a hash parameter telling that markup can be
   * loaded from storage.
   *
   * @param {class} that, ensure ability to access page data
   */
  _useSessionStorage(that) {
    if (window.sessionStorage) {
      if (location.hash.indexOf('|_s') > -1) {
        try {
          const storageKey = that._createStorageKey();
          const data = JSON.parse(sessionStorage.getItem(storageKey));
          if (data) {
            $('.search-result-rows').html(data.markup);
            that._page = data.page;
            const results = that.$dom.searchResults.find('.car-card').length;
            const total = $(DOM.resultsTotal).text();
            that._updateResultsProgress(results, total, results < total);
            sessionStorage.removeItem(storageKey);
            history.replaceState(null, null, location.pathname + (location.search || '')); // remove hash
          }
        } catch (e) {} // eslint-disable-line no-empty
      }

      this.$dom.searchResults.on('click', '.card.car-card.card--product a', function (e) {
        e.preventDefault();
        $('.favorites__like').each((index, element) => element.classList.remove('initialized'));
        const markup = $(this).closest('.search-result-rows').html();
        const data = {
          page: that._page,
          markup,
        };

        try {
          const storageKey = that._createStorageKey();
          sessionStorage.setItem(storageKey, JSON.stringify(data));
        } catch (e) {} // eslint-disable-line no-empty
        history.replaceState(null, null, '#|_s');
        window.location.href = e.currentTarget.href;
      });
    }
  }

  /**
   * Create session storage key from current page url
   * @returns {string}
   */
  _createStorageKey() {
    return 'searchPage_' + location.pathname + (location.search || '');
  }

  _onColorClicked(event) {
    $(event.currentTarget).toggleClass('colorsfilter__color--active');
    this.$dom.searchResults.find('.loading').show();
  }

  _onRegInput(event) {
    var isMobile = window.matchMedia('only screen and (max-width: 1024px)').matches;
    // event.which === 13 checks that the event fired was the enter key specifically.
    if (event.which === 13 && !isMobile) {
      $('html, body').animate({
        scrollTop: '1px',
      });
    }
  }

  /**
   * Reset form inputs
   *
   * @param {object} [options={}] options for resetting
   * @param {string} options.ignoredInput name of input that will be ignored
   * @memberof ProductFinderForm
   */
  resetForm(options = {}) {
    const form = document.querySelector('.product-finder-form');
    const formInputs = form.querySelectorAll(
      'select, input[type="checkbox"], input[type="text"], input[type="radio"], input[type="hidden"]',
    );

    Array.from(formInputs).forEach(control => {
      let somethingChanged = false;

      if (options.ignoredInput && control.name === options.ignoredInput) return;

      if (control.tagName.toLowerCase() === 'select') {
        control.value = '';
        somethingChanged = true;
      } else if (control.checked) {
        control.checked = false;
        somethingChanged = true;
      } else if (control.type === 'text' || control.type === 'hidden') {
        control.value = '';
        somethingChanged = true;
      }

      if (somethingChanged) {
        control.dispatchEvent(new Event('change'));
      }
    });
  }

  _serializeToggableValue(selector, name) {
    return this.$dom.form.find(selector).map((i, e) => {
      return { name: name, value: $(e).val() };
    });
  }

  _serializeArray() {
    let params = this.$dom.form.serializeArray();

    return params.filter(searchCriteria => {
      if (_.isNumber(searchCriteria.value) && !_.isNaN(searchCriteria.value)) {
        return true;
      }
      return !_.isEmpty(searchCriteria.value);
    });
  }

  _updateSearchResultHeader(response, params) {
    const $title = this.$dom.searchResultsHeader.find('.search-result__title');
    const heading = response.listDetails.heading || 'Hakutuloksia';
    const $description = this.$dom.searchResultsHeader.find('#product_finder_corresponding_vehicles');

    $title.find('.title-text__heading').html(heading);
    $title.find('.amount').html(response.resultAvailable);

    const parametersNotIncludedInURL = ['vehicleUuids', 'campaigns', 'page', 'sort'];
    const paramsToKeep = ['manufacturer', 'model', 'search_type'];

    if (response !== null && response.resultAvailable === 0) {
      const href = this.$dom.searchResultsHeader.find('#product_finder_corresponding_vehicles a').attr('href').split('?')[0];

      //remove some defined items from params
      params = params.filter(param => {
        const key = param.name.replace('[]', '');
        return parametersNotIncludedInURL.indexOf(key) === -1;
      });

      //keep only neccessary parameters
      let _params = params.filter(param => {
        const key = param.name.replace('[]', '');
        return paramsToKeep.indexOf(key) !== -1;
      });

      //and search params are still equal, remove also model
      if (Object.keys(_params).length === Object.keys(params).length) {
        _params = params.filter(param => param.name !== 'model[]');
      }

      //create query string for link
      const sep = '&';
      let qs = '';

      Object.entries(_params).forEach(([key, elem], index) => {
        if (typeof elem === 'object') {
          qs += (index === 0 ? '' : sep) + `${elem.name}=${elem.value}`;
        }
      });

      this.$dom.searchResultsHeader.find('#product_finder_corresponding_vehicles a').attr('href', href + '?' + qs);
      //show information text and link
      $description.removeClass('d-none');
      this.$dom.searchResultsHeader.find('.list-details').html('');
    } else {
      //hide information text and link
      $description.addClass('d-none');
      this.$dom.searchResultsHeader.find('.list-details').html(response.listDetails.description);
    }

    this._updateSearchResultsAmount(response);
  }

  /**
   * Update search results amount to match search response
   * @param {object} response search response
   */
  _updateSearchResultsAmount(response) {
    // update amount in product finder header
    document.querySelector('.search-result__amount .amount').innerHTML = response.resultAvailable;
    // update amount in mobile search criteria
    document.querySelector('span.result-amount').innerHTML = response.resultAvailable;
  }

  /**
   * Update document title to match search response
   * @param {object} response search response
   */
  _updateTitle(response) {
    if (response.listDetails && response.listDetails.heading) {
      document.title = response.listDetails.heading + ' | J. Rinta-Jouppi';
    }
  }

  /**
   * Update search fields visibility for specific search types
   *
   * @param {Array} [params=[]] query parameters
   * @memberof ProductFinderForm
   */
  _updateSearchFieldsVisibility(params) {
    const selectedSearchTypes = this._getSelectedSearchTypes(params);
    const showSearchTypes = selectedSearchTypes.includes('moottoripyorat') ? false : true;
    const excludedFromSearchTypes = document.querySelectorAll('[data-key="usedandnewcars"]');
    const includedInSearchTypes = document.querySelectorAll('[data-key="min_cylinder_vol"], [data-key="max_cylinder_vol"]');

    for (const inputContainer of excludedFromSearchTypes) {
      if (showSearchTypes) {
        inputContainer.style.removeProperty('display');
      } else {
        inputContainer.style.setProperty('display', 'none', 'important'); // !important is needed since these have display: flex !important;
      }
    }

    for (const inputContainer of includedInSearchTypes) {
      if (showSearchTypes) {
        inputContainer.style.setProperty('display', 'none', 'important'); // !important is needed since these have display: flex !important;
      } else {
        inputContainer.style.removeProperty('display');
      }
    }
    // show input container license_class if search type is moottoripyörät
    const licenseClassContainer = document.querySelector('[data-key="license_class"]');
    if (licenseClassContainer) {
      if (selectedSearchTypes.includes('moottoripyorat')) {
        licenseClassContainer.style.removeProperty('display');
        licenseClassContainer.querySelector('select').disabled = false;
      } else {
        licenseClassContainer.style.setProperty('display', 'none', 'important');
        licenseClassContainer.querySelector('select').disabled = true;
      }
    }
  }

  /**
   * add required mustache parameters to response data
   * @param {object} response search response
   * @return {object} response
   */
  _setupCarCard(response) {
    response.column = 'col-sm-6 col-lg-4 col-xxl-3';
    response.class = 'car-card card--product';
    response.includeRank = response.params && response.params.rank_max;
    return response;
  }

  /**
   * Product finder's search can be limited to smaller group vehicles
   * by embedding <script class="vehicleuuids" type="application/json">[ vehicle.uuid, ... ]</script>
   * tag to DOM.
   *
   * Search element from this.$el and try to parse content. If content is found it is passed to Ajax query.
   *
   * @private
   * @return {string[]} vehicle's uuids in array or empty array
   */
  _getVehicleUuids() {
    if (this.$el.find('.campaign').length) {
      try {
        return JSON.parse(this.$el.find('.vehicleuuids').html());
      } catch (error) {
        console.error('Could not parse vehicle ids from read from script.vehicleuuids element.', error);
      }
    }
    return [];
  }

  /**
   * Product finder's search can be limited to smaller group vehicles
   * by embedding <script class="campaign" type="application/json">{ campaign: 'campaign.uuid' }</script>
   * tag to DOM.
   *
   * Search element from this.$el and try to parse content. If content is found it is passed to Ajax query.
   *
   * @private
   * @return {string[]} vehicle's uuids in array or empty array
   */
  _getCampaignsUuids() {
    if (this.$el.find('.campaigns').length) {
      try {
        return JSON.parse(this.$el.find('.campaigns').html());
      } catch (error) {
        console.error('Could not parse campaign id from read from script.campaign element.', error);
      }
    }
    return [];
  }

  /**
   * Send product finder's search to data layer (JRINTCONT-767). Searches are only sent for custom searches
   * (i.e. no hard-coded searches or searches without user-input parameters). Search parameters are parsed
   * from GET and sent as part of the event.
   * @private
   */
  _handleDataLayerPush() {
    // We're only interested in user-input custom searches, not hardcoded search pages.
    if (!window.location.search) return;
    if (window.location.pathname !== '/haku/') return;

    // parse GET params
    let getParams = window.location.search.substring(1).split('&');
    let searchParams = {};
    for (const param of getParams) {
      const paramParts = param.split('=');
      let key = decodeURIComponent(paramParts[0]);
      const value = decodeURIComponent(paramParts[1]);
      if (key.endsWith('[]')) {
        key = key.substring(0, key.length - 2);
        searchParams[key] ? searchParams[key].push(value) : (searchParams[key] = [value]);
      } else {
        searchParams[key] = value;
      }
    }

    datalayerActions.pushObjectToDatalayer({ event: 'car_search_done', ...searchParams });
  }
}

function init() {
  if ($('.product-finder').length > 0) {
    const filterForm = new ProductFinderForm('.product-finder');
    filterForm.attach();
  }
}

export default { init, ProductFinderForm };
export { init, ProductFinderForm };
